import { reactive, watch, computed } from 'vue';
import { useIntersectionObserver, useWindowScroll, useWindowSize, useElementBounding } from '@vueuse/core';

// Configuration constants
const ELEMENT_SIZE_THRESHOLD = 1.2; // Elements larger than this use visibility logic
//const SCENE_CHANGE_BLEND_RATE = 0.05; // Interpolation speed during scene changes
const INITIAL_UPDATE_DELAY = 200; // Delay before first effect calculation

export function useElementTracker() {
  const trackedElements = reactive([]);

  const addElement = (el, id) => {
    if (!trackedElements.find((item) => item.el === el)) {
      const bounding = useElementBounding(el);
      const { y: scrollY } = useWindowScroll();
      const { height: windowHeight } = useWindowSize();

      const elementData = reactive({
        id: id,
        inView: false,
        effectCount: 0
      });

      trackedElements.push(elementData);

      const observeElements = () => useIntersectionObserver(el, ([entry]) => {
        elementData.inView = entry.isIntersecting;
        if(elementData.inView) {
          updateEffect(elementData, bounding, windowHeight)
        };
      }, { threshold: [0, 0.5, 1], rootMargin: '0px' });

      setTimeout(() => observeElements(), INITIAL_UPDATE_DELAY);
      watch(scrollY, () => updateEffect(elementData, bounding, windowHeight));
      watch(windowHeight, () => updateEffect(elementData, bounding, windowHeight));
    }
  };

  const updateEffect = (elementData, bounding, windowHeight) => {
    const isVisible = bounding.top.value < window.innerHeight && bounding.bottom.value > 0;
    const visibleHeight = Math.min(bounding.bottom.value, windowHeight.value) - Math.max(bounding.top.value, 0);

    elementData.inView = isVisible && visibleHeight > 0;

    if (!elementData.inView) {
      elementData.effectCount = 0;
      elementData.logic = 'none';
      return;
    }

    const windowHeightValue = Number.isFinite(windowHeight.value) ? windowHeight.value : window.innerHeight;
    const boundingHeight = Number.isFinite(bounding.height.value) ? bounding.height.value : 0;
    const boundingTop = Number.isFinite(bounding.top.value) ? bounding.top.value : 0;
    const elementMiddle = boundingTop + (boundingHeight / 2);
    const viewportMiddle = windowHeightValue / 2;
    const distanceFromMiddle = Math.abs(elementMiddle - viewportMiddle);
    const maxDistance = windowHeightValue / 2;

    const visibilityEffect = Math.min(visibleHeight / windowHeightValue, 1) * 100;
    const centerEffect = maxDistance > 0 ? 100 - (distanceFromMiddle / maxDistance) * 100 : 0;

    // Only use visibility logic if element is larger than threshold
    const elementHeight = bounding.height.value;
    const useVisibility = elementHeight > (windowHeightValue * ELEMENT_SIZE_THRESHOLD);

    if (useVisibility) {
      elementData.effectCount = Math.max(centerEffect, visibilityEffect, 0);
      elementData.logic = centerEffect > visibilityEffect ? 'center' : 'visibility';
    } else {
      elementData.effectCount = Math.max(centerEffect, 0);
      elementData.logic = 'center';
    }
  };

  const removeElement = (el) => {
    const index = trackedElements.findIndex((item) => item.el === el);
    if (index !== -1) {
      trackedElements.splice(index, 1);
    }
  };

  const topScenes = computed(() => {
    // Get all visible scenes sorted by effect count
    const sorted = trackedElements
      .filter(item => item.inView)
      .sort((a, b) => b.effectCount - a.effectCount);

    // Take top N scenes (up to 5)
    const topN = sorted.slice(0, 5);

    // Calculate total effect for normalization
    const totalEffect = topN.reduce((sum, scene) => sum + scene.effectCount, 0);

    // Return scenes with normalized weights
    if (totalEffect === 0) {
      return topN.map(scene => ({ ...scene, weight: 0 }));
    }

    return topN.map(scene => ({
      ...scene,
      weight: scene.effectCount / totalEffect
    }));
  });

  // Backward compatibility - keep topTwoScenes for existing code
  const topTwoScenes = computed(() => {
    const scenes = topScenes.value;
    return {
      primary: scenes[0] || null,
      secondary: scenes[1] || null
    };
  });

  // Backward compatibility - interpolation factor between top 2
  const interpolationFactor = computed(() => {
    const scenes = topScenes.value;
    if (!scenes[0]) return 0.5;
    return scenes[0].weight;
  });

  return {
    trackedElements,
    topScenes,
    topTwoScenes,
    interpolationFactor,
    addElement,
    removeElement,
  };
}
